import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  usePrevious,
  FormErrorMessage,
  Tooltip,
  Icon,
  useToast,
  Skeleton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { RootReducer } from "../../../RootReducer";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  FetchCountryOptions,
  FetchGenderOptions,
  FetchOSOptions,
  PersistCampaignDetailsAction,
  UpdateFetchedIconUrlAction,
  ValidateAppAction,
  FetchCampaignDetails,
} from "./action";
import { CampaignDetails } from "../default/reducer";
import { Select } from "chakra-react-select";
import { NavLink, useHistory } from "react-router-dom";
import { MdHelp } from "react-icons/md";
import { useQuery } from "hooks/useQuery";
import moment from "moment";
import { Post } from "api";
import {
  showGlobalLoading,
  hideGlobalLoading,
} from "helper/GlobalLoadingDispatches";

const mmpOptions = [
  {
    label: "AppsFlyer",
    value: "appsflyer",
  },
  {
    label: "Adjust",
    value: "adjust",
  },
  {
    label: "Singular",
    value: "singular",
  },
  {
    label: "Tenjin",
    value: "tenjin",
  },
];

const biddingTypeOptions = [
  {
    label: "CPI",
    value: "cpi",
  },
  {
    label: "CPA",
    value: "cpa",
  },
];

const platformOptions = [
  { label: "Android", value: "android" },
  { label: "iOS", value: "ios" },
];

const enableAdvancedTargetingForNewCampaigns = false;

export default function CampaignCreate(props: any) {
  const toast = useToast();
  const dispatch = useDispatch();
  const history = useHistory();
  const searchParams = useQuery();
  const campaignId = searchParams.get("id");
  const isCampaignCreate = !campaignId;

  const [isDirect, setIsDirect] = useState(true);
  const canEditCampaign = isCampaignCreate ? true : isDirect;
  const [showAgeTargeting, setShowAgeTargeting] = useState(false);
  const [showGenderTargeting, setShowGenderTargeting] = useState(false);
  const [showOSTargeting, setShowOSTargeting] = useState(false);

  // Keep track of initial campaign values to determine targeting visibility
  const [initialCampaignLoaded, setInitialCampaignLoaded] = useState(false);

  const [campaign, setCampaign] = useState<CampaignDetails>({
    bid: 0,
    biddingType: "cpi",
    clickUrl: "",
    countries: undefined,
    dailyBudgetCap: 0,
    genders: undefined,
    id: "",
    maxAge: undefined,
    maxApiLevel: undefined,
    minAge: undefined,
    minApiLevel: undefined,
    name: "",
    mmp: "appsflyer",
    playStoreUrl: "",
    iconUrl: "",
    platform: "android",
    appStoreUrl: "",
  });

  useEffect(() => {
    dispatch(FetchOSOptions());
    dispatch(FetchGenderOptions());
    dispatch(FetchCountryOptions());

    if (!isCampaignCreate && campaignId) {
      dispatch(FetchCampaignDetails(campaignId));
    }
  }, [dispatch, isCampaignCreate, campaignId]);

  useEffect(() => {
    if (!isCampaignCreate && !initialCampaignLoaded) {
      const hasAgeTargeting = !!campaign.minAge || !!campaign.maxAge;
      const hasGenderTargeting =
        !!campaign.genders &&
        Array.isArray(campaign.genders) &&
        campaign.genders.length > 0;
      const hasOSTargeting = !!campaign.minApiLevel || !!campaign.maxApiLevel;

      // Only set targeting visibility when initial campaign is loaded
      if (campaign.id !== "") {
        setShowAgeTargeting(hasAgeTargeting);
        setShowGenderTargeting(hasGenderTargeting);
        setShowOSTargeting(hasOSTargeting);
        setInitialCampaignLoaded(true);
      }
    } else if (isCampaignCreate && !initialCampaignLoaded) {
      // For new campaigns, use the global setting
      setShowAgeTargeting(enableAdvancedTargetingForNewCampaigns);
      setShowGenderTargeting(enableAdvancedTargetingForNewCampaigns);
      setShowOSTargeting(enableAdvancedTargetingForNewCampaigns);
      setInitialCampaignLoaded(true);
    }
  }, [
    isCampaignCreate,
    campaign.id,
    campaign.minAge,
    campaign.maxAge,
    campaign.genders,
    campaign.minApiLevel,
    campaign.maxApiLevel,
    initialCampaignLoaded,
  ]);

  const campaignDetails = useSelector(
    (state: RootReducer) => state.campaignDetails.campaignDetails
  );
  const fetchCampaignRequestState = useSelector(
    (state: RootReducer) => state.campaignDetails.fetchCampaignRequestState
  );
  const osOptions = useSelector(
    (state: RootReducer) => state.campaignDetails.osOptions
  );
  const genderOptions = useSelector(
    (state: RootReducer) => state.campaignDetails.genderOptions
  );
  const countryOptions = useSelector(
    (state: RootReducer) => state.campaignDetails.countryOptions
  );

  const showBiddingType = localStorage.getItem("b") === "1";
  const showPlatformSelect = localStorage.getItem("p") === "1";
  const accountInfo = useSelector(
    (state: RootReducer) => state.authentication.accountInfo
  );

  let showPlatformSelection = false;
  if (
    (accountInfo && accountInfo.showPlatformSelection) ||
    showPlatformSelect
  ) {
    showPlatformSelection = true;
  }

  const iconUrl = useSelector(
    (state: RootReducer) => state.campaignDetails.fetchedAppIconUrl
  );
  const campaignRequestState = useSelector(
    (state: RootReducer) => state.campaignDetails.updateCampaignRequestState
  );
  const previousCampaignRequestState = usePrevious(campaignRequestState);

  const [selectedMinApiLevel, setSelectedMinApiLevel] = useState(() =>
    campaign.platform === "ios"
      ? undefined
      : campaign.minApiLevel
      ? osOptions.find((o) => o.value === campaign.minApiLevel)
      : undefined
  );
  const [selectedMaxApiLevel, setSelectedMaxApiLevel] = useState(() =>
    campaign.platform === "ios"
      ? undefined
      : campaign.maxApiLevel
      ? osOptions.find((o) => o.value === campaign.maxApiLevel)
      : undefined
  );

  useEffect(() => {
    if (campaign.platform === "ios") {
      setSelectedMinApiLevel(undefined);
      setSelectedMaxApiLevel(undefined);
    } else {
      setSelectedMinApiLevel(
        campaign.minApiLevel
          ? osOptions.find((o) => o.value === campaign.minApiLevel)
          : undefined
      );
      setSelectedMaxApiLevel(
        campaign.maxApiLevel
          ? osOptions.find((o) => o.value === campaign.maxApiLevel)
          : undefined
      );
    }
  }, [
    campaign.platform,
    campaign.minApiLevel,
    campaign.maxApiLevel,
    osOptions,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isCampaignCreate) {
      setCampaign({
        bid: 0,
        biddingType: "cpi",
        clickUrl: "",
        countries: undefined,
        dailyBudgetCap: 0,
        genders: undefined,
        id: "",
        maxAge: undefined,
        maxApiLevel: undefined,
        minAge: undefined,
        minApiLevel: undefined,
        name: "",
        mmp: "appsflyer",
        playStoreUrl: "",
        iconUrl: "",
        platform: "android",
        appStoreUrl: "",
      });
    }
  }, [isCampaignCreate]);

  useEffect(() => {
    if (campaign.platform === "ios") {
      setCampaign((prevCampaignData) => ({
        ...prevCampaignData,
        maxApiLevel: undefined,
        minApiLevel: undefined,
      }));
    }
  }, [campaign.platform]);

  useEffect(() => {
    if (
      campaignRequestState.isSuccessful &&
      previousCampaignRequestState &&
      previousCampaignRequestState.isLoading
    ) {
      history.goBack();
    }
  }, [history, campaignRequestState, previousCampaignRequestState]);

  useEffect(() => {
    if (!isCampaignCreate && campaignDetails) {
      setCampaign({
        bid: campaignDetails.bid,
        biddingType: campaignDetails.biddingType,
        clickUrl: campaignDetails.clickUrl,
        countries: campaignDetails.countries,
        genders: campaignDetails.genders,
        minAge: campaignDetails.minAge,
        maxAge: campaignDetails.maxAge,
        minApiLevel: campaignDetails.minApiLevel,
        maxApiLevel: campaignDetails.maxApiLevel,
        dailyBudgetCap: campaignDetails.dailyBudgetCap,
        playStoreUrl: campaignDetails.playStoreUrl,
        appStoreUrl: campaignDetails.appStoreUrl,
        iconUrl: campaignDetails.iconUrl,
        mmp: campaignDetails.mmp || "appsflyer",
        id: campaignDetails.id,
        name: campaignDetails.name,
        platform: campaignDetails.platform ?? "android",
      });

      if ((campaignDetails as any).isDirect !== undefined) {
        setIsDirect((campaignDetails as any).isDirect);
      }

      if (campaignDetails.playStoreUrl && !campaignDetails.iconUrl) {
        dispatch(ValidateAppAction(campaignDetails.playStoreUrl));
      }

      setInitialCampaignLoaded(false);
    }
  }, [campaignDetails, isCampaignCreate]);

  useEffect(() => {
    dispatch(UpdateFetchedIconUrlAction(campaign.iconUrl));
  }, [campaign.iconUrl]);

  useEffect(() => {
    if (campaign.clickUrl.includes("adjust")) {
      setCampaign((c) => ({
        ...c,
        mmp: "adjust",
      }));
    } else if (campaign.clickUrl.includes(".sng.link")) {
      setCampaign((c) => ({
        ...c,
        mmp: "singular",
      }));
    } else if (campaign.clickUrl.includes("tenjin")) {
      setCampaign((c) => ({
        ...c,
        mmp: "tenjin",
      }));
    } else {
      setCampaign((c) => ({
        ...c,
        mmp: "appsflyer",
      }));
    }
  }, [campaign.clickUrl]);

  useEffect(() => {
    if (campaign.bid) {
      setCpi(campaign.bid.toFixed(2));
    } else {
      setCpi("0.00");
    }
  }, [campaign.bid]);

  const [cpi, setCpi] = useState<string>("");

  const selectedGenders = campaign.genders
    ? genderOptions.filter((o) => campaign.genders.includes(Number(o.value)))
    : undefined;
  const selectedCountries = campaign.countries
    ? countryOptions.filter((o) =>
        campaign.countries.includes(o.value.toString())
      )
    : undefined;
  const selectedPlatform = campaign.platform
    ? platformOptions.find((o) => o.value === campaign.platform)
    : undefined;
  const selectedMMP = campaign.mmp
    ? mmpOptions.find((o) => o.value === campaign.mmp)
    : undefined;
  const selectedBiddingType = campaign.biddingType
    ? biddingTypeOptions.find((o) => o.value === campaign.biddingType)
    : undefined;

  const style = {
    backgroundColor: "white",
    borderRadius: "8px",
  };

  const formControlStyle = {
    marginBottom: "20px",
  };

  const handleValidateClickTrackingURL = useCallback(
    async ({
      storeURL,
      clickTrackingURL,
      platformId,
      mmp,
    }: {
      storeURL: string;
      clickTrackingURL: string;
      platformId: number;
      mmp: "appsflyer" | "singular" | "adjust" | "tenjin";
    }) => {
      if (!storeURL) return;

      try {
        const params = new URLSearchParams(new URL(storeURL).search);
        const storeId = params.get("id");

        const response = await Post("/dashboard/link-builder/validate", {
          storeId: storeId,
          trackingUrl: clickTrackingURL,
          platformId: platformId,
          mmp: mmp,
        });
        if (response.error) {
          let message = "";
          if (response.error.endsWith(".")) {
            message = `${response.error} In case you need further assistance, please consult with your AM.`;
          } else {
            message = `${response.error}. In case you need further assistance, please consult with your AM.`;
          }
          toast({
            title: "Error",
            description: message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });

          return;
        }

        return true;
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description:
            "An error occurred while validating the Click Tracking URL.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    },
    [toast]
  );

  const optionsLoaded = useMemo(() => {
    return (
      countryOptions.length > 0 &&
      genderOptions.length > 0 &&
      osOptions.length > 0
    );
  }, [countryOptions.length, genderOptions.length, osOptions.length]);

  const isDataReady = isCampaignCreate
    ? optionsLoaded
    : optionsLoaded && fetchCampaignRequestState.isSuccessful;

  return (
    <Flex flexDirection={"row"} width={"1200px"}>
      {isDataReady ? (
        <>
          <Box
            width={"800px"}
            padding={"20px"}
            marginTop={"20px"}
            pt={{ base: "130px", md: "80px", xl: "80px" }}
          >
            {/* Main Fields */}

            <FormControl style={formControlStyle} id="first-name" isRequired>
              <FormLabel>Campaign Name</FormLabel>
              <Input
                value={campaign.name}
                style={style}
                placeholder="US_EN_Test"
                borderRadius="16px"
                onChange={(c) => {
                  setCampaign({ ...campaign, name: c.target.value });
                }}
              />
            </FormControl>

            {showPlatformSelection ? (
              <FormControl style={formControlStyle} id="platform" isRequired>
                <div>
                  <Flex style={{ flexDirection: "row" }}>
                    <FormLabel>Platform</FormLabel>
                  </Flex>
                  <div style={style}>
                    <Select
                      isDisabled={!isCampaignCreate || !canEditCampaign}
                      value={selectedPlatform}
                      isMulti={false}
                      placeholder={"Select"}
                      options={platformOptions}
                      onChange={(v) => {
                        const platform = v.value as "android" | "ios";
                        setCampaign({ ...campaign, platform });
                      }}
                      selectedOptionColor="brand"
                    />
                  </div>
                </div>
              </FormControl>
            ) : (
              <></>
            )}

            <SimpleGrid
              columns={campaign.platform === "android" ? 1 : 1}
              spacing={0}
              mt={4}
            >
              {campaign.platform === "ios" ? (
                <FormControl
                  style={formControlStyle}
                  id="appstore-url"
                  isRequired
                >
                  <FormLabel>App Store URL</FormLabel>
                  <Input
                    defaultValue={campaign.appStoreUrl}
                    style={style}
                    placeholder="https://apps.apple.com/us/app/id123456789"
                    borderRadius="16px"
                    isDisabled={!canEditCampaign}
                    onChange={(c) => {
                      setCampaign({
                        ...campaign,
                        appStoreUrl: c.target.value,
                      });
                    }}
                  />
                </FormControl>
              ) : (
                <></>
              )}

              <FormControl
                style={formControlStyle}
                id="google-playstore-url"
                isRequired
              >
                <FormLabel>
                  {campaign.platform === "ios"
                    ? "Google PlayStore URL (required for the setup)"
                    : "Google PlayStore URL"}
                </FormLabel>
                <Input
                  defaultValue={campaign.playStoreUrl}
                  style={style}
                  placeholder="https://play.google.com/store/apps/details?id=com.example.app"
                  borderRadius="16px"
                  isDisabled={!canEditCampaign}
                  onChange={(c) => {
                    setCampaign({
                      ...campaign,
                      playStoreUrl: c.target.value,
                    });
                    dispatch(ValidateAppAction(c.target.value));
                  }}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={10} mt={4}>
              <FormControl
                style={{ marginBottom: "20px", zIndex: 30 }}
                id="country"
              >
                <FormLabel>Country</FormLabel>

                <div style={style}>
                  <Select
                    isDisabled={!canEditCampaign}
                    onChange={(v) => {
                      let a: string[] = [];

                      v.forEach((vo) => a.push(vo.value.toString()));

                      setCampaign({
                        ...campaign,
                        countries: a,
                      });
                    }}
                    value={selectedCountries}
                    isMulti={true}
                    placeholder={"All"}
                    options={countryOptions}
                    selectedOptionColor="brand"
                    isClearable
                    chakraStyles={{
                      dropdownIndicator: (prev) => ({
                        ...prev,
                        px: 1,
                      }),
                      clearIndicator: (prev) => ({
                        ...prev,
                        width: 6,
                        fontSize: "0.6em",
                      }),
                      menuList: (props) => ({
                        ...props,
                        maxHeight: "30vh",
                      }),
                      menu: (props) => ({
                        ...props,
                        maxHeight: "30vh",
                      }),
                    }}
                  />
                </div>
              </FormControl>

              {showOSTargeting && (campaign.platform as string) !== "ios" && (
                <SimpleGrid columns={2} spacing={4}>
                  <div>
                    <FormLabel>Min OS</FormLabel>
                    <div style={style}>
                      <Select
                        isDisabled={
                          (campaign.platform as string) === "ios" ||
                          !canEditCampaign
                        }
                        value={selectedMinApiLevel ?? null}
                        isMulti={false}
                        placeholder={"All"}
                        isClearable
                        chakraStyles={{
                          dropdownIndicator: (prev) => ({
                            ...prev,
                            px: 1,
                          }),
                          clearIndicator: (prev) => ({
                            ...prev,
                            width: 6,
                            fontSize: "0.6em",
                          }),
                          menuList: (props) => ({
                            ...props,
                            maxHeight: "30vh",
                          }),
                          menu: (props) => ({
                            ...props,
                            maxHeight: "30vh",
                          }),
                        }}
                        options={osOptions}
                        onChange={(v) => {
                          if (!v) {
                            setCampaign({
                              ...campaign,
                              minApiLevel: null,
                            });
                            return;
                          }

                          const value = Number(v.value);
                          const maxOsValue = campaign.maxApiLevel;

                          if (maxOsValue && value > maxOsValue) {
                            toast({
                              title: "Error",
                              description:
                                "Min OS version should be equal or less than max OS version",
                              status: "error",
                              duration: 5000,
                              isClosable: true,
                            });
                            return;
                          }

                          setCampaign({
                            ...campaign,
                            minApiLevel: Number(v.value),
                          });
                        }}
                        selectedOptionColor={"brand"}
                      />
                    </div>
                  </div>
                  <div>
                    <FormLabel>Max OS</FormLabel>
                    <div style={style}>
                      <Select
                        value={selectedMaxApiLevel ?? null}
                        isMulti={false}
                        isDisabled={
                          (campaign.platform as string) === "ios" ||
                          !canEditCampaign
                        }
                        placeholder={"All"}
                        isClearable
                        chakraStyles={{
                          dropdownIndicator: (prev) => ({
                            ...prev,
                            px: 1,
                          }),
                          clearIndicator: (prev) => ({
                            ...prev,
                            width: 6,
                            fontSize: "0.6em",
                          }),
                          menuList: (props) => ({
                            ...props,
                            maxHeight: "30vh",
                          }),
                          menu: (props) => ({
                            ...props,
                            maxHeight: "30vh",
                          }),
                        }}
                        onChange={(v) => {
                          if (!v) {
                            setCampaign({
                              ...campaign,
                              maxApiLevel: null,
                            });
                            return;
                          }
                          const value = Number(v.value);
                          const minOsValue = campaign.minApiLevel;

                          if (minOsValue && value < minOsValue) {
                            toast({
                              title: "Error",
                              description:
                                "Max OS version should be equal or greater than min OS version",
                              status: "error",
                              duration: 5000,
                              isClosable: true,
                            });
                            return;
                          }

                          setCampaign({
                            ...campaign,
                            maxApiLevel: Number(v.value),
                          });
                        }}
                        options={osOptions}
                        selectedOptionColor="brand"
                      />
                    </div>
                  </div>
                </SimpleGrid>
              )}
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={10}>
              {showGenderTargeting && (
                <FormControl
                  style={{ marginBottom: "20px", zIndex: 25 }}
                  id="gender"
                >
                  <FormLabel>Gender</FormLabel>

                  <div style={style}>
                    <Select
                      onChange={(v) => {
                        let a: number[] = [];

                        v.forEach((vo) => a.push(Number(vo.value)));

                        setCampaign({
                          ...campaign,
                          genders: a,
                        });
                      }}
                      value={selectedGenders}
                      isMulti={true}
                      placeholder={"All"}
                      options={genderOptions}
                      selectedOptionColor="brand"
                      isDisabled={!canEditCampaign}
                      isClearable
                      chakraStyles={{
                        dropdownIndicator: (prev) => ({
                          ...prev,
                          px: 1,
                        }),
                        clearIndicator: (prev) => ({
                          ...prev,
                          width: 6,
                          fontSize: "0.6em",
                        }),
                      }}
                    />
                  </div>
                </FormControl>
              )}

              {showAgeTargeting && (
                <SimpleGrid columns={2} spacing={4}>
                  <div>
                    <FormLabel>Min Age</FormLabel>

                    <NumberInput
                      defaultValue={campaign?.minAge}
                      onChange={(valueString) => {
                        if (!valueString) {
                          setCampaign({
                            ...campaign,
                            minAge: null,
                          });
                          return;
                        }

                        if (Number(valueString) > 99) {
                          toast({
                            title: "Error",
                            description: "Min age should be less than 99",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                          return;
                        }

                        setCampaign({
                          ...campaign,
                          minAge:
                            Number(valueString) > 0
                              ? Math.floor(Number(valueString))
                              : null,
                        });
                      }}
                      onBlur={(a) => {
                        if (!a.target.value) {
                          setCampaign({
                            ...campaign,
                            minAge: null,
                          });
                          return;
                        }
                        const aAsNumber = Math.floor(Number(a.target.value));
                        const maxAge = campaign.maxAge;

                        if (maxAge && aAsNumber > maxAge) {
                          toast({
                            title: "Error",
                            description:
                              "Min age should be equal or less than max age",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                          setCampaign({
                            ...campaign,
                            minAge: maxAge,
                          });
                          return;
                        }
                      }}
                      style={style}
                      max={campaign?.maxAge ?? 99}
                      min={12}
                      value={campaign?.minAge || ""}
                      isDisabled={!canEditCampaign}
                    >
                      <NumberInputField placeholder={"All"} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </div>
                  <div>
                    <FormLabel>Max Age</FormLabel>
                    <NumberInput
                      onChange={(valueString) => {
                        if (Number(valueString) > 99) {
                          toast({
                            title: "Error",
                            description: "Max age should be less than 99",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                          return;
                        }

                        if (!valueString) {
                          setCampaign({
                            ...campaign,
                            maxAge: null,
                          });
                          return;
                        }

                        setCampaign({
                          ...campaign,
                          maxAge:
                            Number(valueString) > 0
                              ? Math.floor(Number(valueString))
                              : null,
                        });
                      }}
                      onBlur={(a) => {
                        if (!a.target.value) {
                          setCampaign({
                            ...campaign,
                            maxAge: null,
                          });
                          return;
                        }
                        const aAsNumber = Math.floor(Number(a.target.value));
                        const minAge = campaign.minAge;

                        if (minAge && aAsNumber < minAge) {
                          toast({
                            title: "Error",
                            description:
                              "Max age should be equal or greater than min age",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                          setCampaign({
                            ...campaign,
                            maxAge: minAge,
                          });
                          return;
                        }
                      }}
                      style={style}
                      max={99}
                      min={campaign.minAge ?? 12}
                      value={campaign?.maxAge || ""}
                      isDisabled={!canEditCampaign}
                      defaultValue={campaign?.maxAge}
                    >
                      <NumberInputField placeholder={"All"} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </div>
                </SimpleGrid>
              )}
            </SimpleGrid>

            <SimpleGrid columns={4} spacing={10} mt={4}>
              {showBiddingType && (
                <FormControl
                  style={{ marginBottom: "20px", zIndex: 20 }}
                  id="biddingtype"
                >
                  <Flex style={{ flexDirection: "row" }}>
                    <FormLabel>Bidding Type</FormLabel>
                    <div
                      style={{
                        backgroundColor: "#4a34ff",
                        paddingTop: 2,
                        height: 24,
                        borderRadius: 20,
                        paddingLeft: 11,
                        marginLeft: -6,
                      }}
                    >
                      <FormLabel
                        fontSize={14}
                        color={"white"}
                        alignSelf={"center"}
                      >
                        NEW
                      </FormLabel>
                    </div>
                  </Flex>

                  <div style={style}>
                    <Select
                      isDisabled={!isCampaignCreate || !canEditCampaign}
                      onChange={(v) => {
                        setCampaign({
                          ...campaign,
                          biddingType: v.value.toString(),
                        });
                      }}
                      value={selectedBiddingType}
                      isMulti={false}
                      options={biddingTypeOptions}
                      selectedOptionColor="brand"
                    />
                  </div>
                </FormControl>
              )}

              {selectedBiddingType && selectedBiddingType.value === "cpa" && (
                <FormControl
                  style={{ marginBottom: "20px", zIndex: 20 }}
                  id="bidding_type"
                >
                  <Flex style={{ flexDirection: "row" }}>
                    <FormLabel>Action</FormLabel>

                    <Tooltip
                      placement="right-end"
                      label={
                        "Purchase Event is identified automatically from historic data."
                      }
                    >
                      <span
                        style={{
                          alignSelf: "center",
                          fontSize: 18,
                          marginLeft: -4,
                          marginTop: -2,
                        }}
                      >
                        <Icon color={"#727272"} as={MdHelp} />
                      </span>
                    </Tooltip>
                  </Flex>

                  <div style={style}>
                    <Select
                      onChange={() => {}}
                      isDisabled={true || !canEditCampaign}
                      value={{
                        value: "purchase",
                        label: "Purchase",
                      }}
                      isMulti={false}
                      options={[]}
                      selectedOptionColor="brand"
                    />
                  </div>
                </FormControl>
              )}

              <FormControl style={formControlStyle} id="country" isRequired>
                <Flex style={{ flexDirection: "row" }}>
                  <FormLabel>Bid</FormLabel>
                </Flex>

                <InputGroup style={style}>
                  <InputLeftElement
                    pointerEvents="none"
                    color="grey.300"
                    fontSize="1.0em"
                    children="$"
                  />
                  <Input
                    isDisabled={!canEditCampaign}
                    value={cpi}
                    style={style}
                    onBlur={() => {
                      let cpiAsNumber = Number(cpi);

                      if (cpiAsNumber !== cpiAsNumber) {
                        cpiAsNumber = 0;
                      }

                      setCampaign({
                        ...campaign,
                        bid: cpiAsNumber,
                      });
                    }}
                    onChange={(c) => {
                      const inputValue = c.target.value;

                      const regex = /^\d*\.?\d*$/;

                      if (regex.test(inputValue)) {
                        setCpi(inputValue);
                      }
                    }}
                  />
                </InputGroup>
              </FormControl>

              <FormControl style={formControlStyle} id="country" isRequired>
                <FormLabel>Daily Budget Cap</FormLabel>
                <InputGroup style={style}>
                  <InputLeftElement
                    pointerEvents="none"
                    color="grey.300"
                    fontSize="1.0em"
                    children="$"
                  />
                  <NumberInput
                    isDisabled={!canEditCampaign}
                    onChange={(c) => {
                      setCampaign({
                        ...campaign,
                        dailyBudgetCap: Number(c) > 0 ? Number(c) : 0,
                      });
                    }}
                    value={campaign.dailyBudgetCap}
                    style={style}
                  >
                    <NumberInputField
                      style={{ paddingLeft: 30 }}
                      placeholder={"Enter amount"}
                    ></NumberInputField>
                  </NumberInput>
                </InputGroup>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={10}>
              <FormControl style={formControlStyle} id="first-name" isRequired>
                <FormLabel>Click Tracking URL</FormLabel>
                <Input
                  value={campaign.clickUrl}
                  style={style}
                  isDisabled={!canEditCampaign}
                  placeholder="https://example.sng.link/Danx5/y3pt?andi={andi}&aifa={aifa}&ve={os}..."
                  borderRadius="16px"
                  onChange={(c) => {
                    setCampaign({ ...campaign, clickUrl: c.target.value });
                  }}
                />
              </FormControl>
              <FormControl
                isInvalid={selectedMMP && selectedMMP.value === "other"}
                style={formControlStyle}
                id="mmp"
              >
                <FormLabel>Mobile Measurement Provider</FormLabel>

                <div style={style}>
                  <Select
                    isDisabled={!canEditCampaign}
                    onChange={(v) => {
                      setCampaign({ ...campaign, mmp: v.value });
                    }}
                    value={selectedMMP}
                    isMulti={false}
                    placeholder={""}
                    options={mmpOptions}
                    selectedOptionColor="brand"
                  />
                </div>
                <FormErrorMessage style={{ marginBottom: 10 }}>
                  Please write us a mail to partners@gamelight.io and we help
                  you with the setup within a few hours.
                </FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            {!isCampaignCreate && (
              <NavLink to={"/ua/cpi"}>
                <Button
                  style={{
                    marginTop: "20px",
                    marginRight: "10px",
                    color: "white",
                    borderColor: "#4a34ff",
                    borderWidth: 2,
                    width: 200,
                  }}
                  color={"#4a34ff"}
                  variant="solid"
                  size="lg"
                >
                  <Text style={{ color: "#4a34ff" }}>{"Cancel"}</Text>
                </Button>
              </NavLink>
            )}
            <Button
              isDisabled={
                campaign.name == "" ||
                campaign.bid == 0 ||
                campaign.dailyBudgetCap == 0 ||
                campaign.playStoreUrl == "" ||
                iconUrl == "" ||
                // eslint-disable-next-line eqeqeq
                campaign.clickUrl == "" ||
                !campaign.mmp ||
                campaign.mmp === "other" ||
                campaignRequestState.isLoading ||
                (!isCampaignCreate && !canEditCampaign)
              }
              onClick={async () => {
                try {
                  showGlobalLoading(
                    isCampaignCreate ? "Creating Campaign" : "Updating Campaign"
                  );

                  if (!isCampaignCreate && !canEditCampaign) return;

                  if (campaignRequestState.isLoading) return;

                  if (!iconUrl) {
                    toast({
                      title: "Error",
                      description: "Please enter a valid PlayStore URL",
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                    return;
                  }
                  const clickTrackinURLIsValıd =
                    await handleValidateClickTrackingURL({
                      storeURL: campaign.playStoreUrl,
                      clickTrackingURL: campaign.clickUrl,
                      platformId: campaign.platform === "android" ? 1 : 2,
                      mmp: campaign.mmp as
                        | "appsflyer"
                        | "singular"
                        | "adjust"
                        | "tenjin",
                    });

                  if (!clickTrackinURLIsValıd) return;

                  dispatch(PersistCampaignDetailsAction(campaign));
                } catch (error) {
                  toast({
                    title: "Error",
                    description: "An error occurred while saving the campaign",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                  });
                } finally {
                  hideGlobalLoading();
                }
              }}
              style={{
                marginTop: "20px",
                color: "white",
                backgroundColor: "#4a34ff",
                width: 200,
              }}
              variant="solid"
              size="lg"
            >
              {isCampaignCreate ? "Launch" : "Save"}
            </Button>
          </Box>
          <div style={{ marginTop: 130, marginLeft: 50 }}>
            <div
              style={{
                width: 200,
                height: 200,
                borderWidth: iconUrl ? 0 : 2,
                borderRadius: 20,
              }}
            >
              {iconUrl && (
                <img
                  style={{ width: 200, height: 200, borderRadius: 20 }}
                  src={iconUrl}
                />
              )}
            </div>

            <Text
              style={{ marginTop: 20, width: 200, color: "#525252" }}
              fontSize="s"
            >
              *All required creatives will be fetched from the PlayStore
            </Text>
          </div>
        </>
      ) : (
        <>
          <Skeleton
            width={"800px"}
            height={"80vh"}
            padding={"20px"}
            marginTop={"20px"}
            mt={{ base: "130px", md: "80px", xl: "80px" }}
          />
          <Skeleton
            width={"200px"}
            height={"200px"}
            padding={"20px"}
            style={{ marginTop: 130, marginLeft: 50 }}
          />
        </>
      )}
    </Flex>
  );
}
